/**
 * Fait : tout ce qui touche aux PARTICIPANTS d'un projet dans sa fiche — la
 *   liste et ses trois gestes (changer un niveau, retirer quelqu'un, partir),
 *   et le formulaire de partage. Elle appelle le serveur, MONTRE le refus s'il
 *   y en a un, puis demande à la fiche de recharger.
 *
 *   Sortie de `PageProjet.tsx` le 2026-08-23, au lot 4, quand la fiche a dépassé
 *   le plafond de lignes du dépôt.
 *
 * POURQUOI C'EST UN COMPOSANT ET NON UN MODULE DE FONCTIONS — mesuré le jour
 *   même : sortis dans un simple `.ts`, les appels de `api-projet.ts`
 *   n'étaient plus employés par aucun écran, et le contrôle des portes est
 *   repassé de 14 défauts à 17. Il avait raison : ce qu'il vérifie, c'est
 *   qu'une route se rejoint depuis quelque chose d'affiché. Découper en
 *   déplaçant du JSX garde cette chaîne entière ; découper en déplaçant des
 *   fonctions la coupe.
 *
 * POURQUOI LES REFUS SONT MONTRÉS ET JAMAIS DEVINÉS : le serveur en a que
 *   l'écran ne saurait pas reconstruire — celui qui protège le référent
 *   (`D-023`), et celui qui dit au fondateur de supprimer le projet plutôt que
 *   de le quitter (`D-032`). Les cacher laisserait des boutons sans effet.
 *
 * Ne fait pas : elle ne décide d'aucun droit — ce qu'elle offre découle du
 *   niveau rendu par l'API — et n'écoute pas le canal : la fiche s'en charge.
 */

import { useState } from 'react';

import type { NiveauDeMembre } from '@aymoai/shared';

import { SectionMembres } from './SectionMembres.tsx';
import { SectionPartage } from './SectionPartage.tsx';
import type { MembreVu, ProjetVu } from './api.ts';
import { changeLeNiveauDUnMembre, partageLeProjet, retireDuProjet } from './api-projet.ts';

export function PartieDesParticipants({
  projet,
  membres,
  presents,
  moiId,
  surEchec,
  recharge,
}: {
  readonly projet: ProjetVu;
  readonly membres: MembreVu[];
  readonly presents: string[];
  readonly moiId: string;
  readonly surEchec: (message: string) => void;
  readonly recharge: () => Promise<void>;
}) {
  const [adresse, setAdresse] = useState('');

  const administre = projet.niveau === 'proprietaire';

  /** Change le niveau de quelqu'un, puis recharge. */
  async function changeLeNiveau(compteId: string, niveau: NiveauDeMembre): Promise<void> {
    const issue = await changeLeNiveauDUnMembre(projet.id, compteId, niveau);

    if (!issue.bon) {
      surEchec(issue.erreur.message);
      return;
    }

    await recharge();
  }

  /** Retire quelqu'un du projet. */
  async function retire(compteId: string): Promise<void> {
    const issue = await retireDuProjet(projet.id, compteId);

    if (!issue.bon) {
      surEchec(issue.erreur.message);
      return;
    }

    await recharge();
  }

  /**
   * QUITTER LE PROJET — la même route que retirer quelqu'un, parce que c'est le
   * même geste vu de l'autre côté. En cas de succès on part vers la liste : la
   * fiche n'a plus d'objet, et y rester afficherait un projet qu'on ne peut
   * plus ouvrir. En cas de refus on RESTE, et on montre la phrase du serveur.
   */
  async function part(): Promise<void> {
    const issue = await retireDuProjet(projet.id, moiId);

    if (!issue.bon) {
      surEchec(issue.erreur.message);
      return;
    }

    location.hash = '#/projets';
  }

  /** Partage le projet avec l'adresse saisie. */
  async function partage(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault();

    const issue = await partageLeProjet(projet.id, adresse.trim(), 'lecture');
    if (!issue.bon) {
      surEchec(issue.erreur.message);
      return;
    }

    setAdresse('');
    await recharge();
  }

  return (
    <>
      <SectionMembres
        membres={membres}
        presents={presents}
        moiId={moiId}
        referentId={projet.referentId}
        peutAdministrer={administre}
        archive={projet.archive}
        surNiveau={(compteId, niveau) => void changeLeNiveau(compteId, niveau)}
        surRetrait={(compteId) => void retire(compteId)}
        surDepart={() => void part()}
      />

      {administre && !projet.archive && (
        <SectionPartage
          adresse={adresse}
          surAdresse={setAdresse}
          surPartage={(evenement) => void partage(evenement)}
        />
      )}
    </>
  );
}
